/**
 * template/sections/waas/WaasPremiumHomeSection.jsx
 *
 * Premium WAAS "AVPS Technologies" home template — READER (published page) side.
 * Converted 1:1 from the standalone HTML/CSS/JS design so it renders IDENTICALLY
 * to the Console builder preview version
 * (admin-console-ui/src/modules/template-builder/sections/WaasPremiumHomeSection.jsx).
 *
 * This is an ADDITIONAL selectable WAAS section type (`waasPremiumHome`) — it
 * does not modify or replace any existing WAAS section, template, or rendering
 * path. It is wired into the section registry exactly like every other WAAS
 * section (see WaasTemplateSections.jsx export + TemplateSectionContentRenderer.jsx
 * + TemplateSectionRegistry.jsx `waasSectionComponents` map).
 */

import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { resolvePresignedUrl } from 'api/action/IOT';
import { getCompanyLogoUrl, getTemplateCompanyContext, selectTemplateCompanyState } from '../../TemplateCompanyUtils';

const PREMIUM_HERO_IMAGE_KEY = '44o676132USF/1784212520872-hero-web.jpg';
const PREMIUM_LOGO_IMAGE_KEY = '44o676132USF/1784212523836-logo.png';

function usePremiumHeroImage(sectionHeroImageUrl) {
  const [heroImage, setHeroImage] = useState('');

  useEffect(() => {
    // If the author has set a custom hero image via section.content, use it directly.
    const override = String(sectionHeroImageUrl || '').trim();
    if (/^(https?:|data:|blob:|\/)/i.test(override)) {
      setHeroImage(override);
      return undefined;
    }
    let isMounted = true;
    resolvePresignedUrl(PREMIUM_HERO_IMAGE_KEY)
      .then((url) => {
        if (isMounted && /^(https?:|data:|blob:)/i.test(String(url || '').trim())) {
          setHeroImage(String(url).trim());
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [sectionHeroImageUrl]);

  return heroImage;
}

function usePremiumLogoImage(sectionLogoUrl) {
  const [logoImage, setLogoImage] = useState('');
  const companyState = useSelector(selectTemplateCompanyState);

  useEffect(() => {
    // If the author has set a custom logo via section.content, use it directly.
    const override = String(sectionLogoUrl || '').trim();
    if (/^(https?:|data:|blob:|\/)/i.test(override)) {
      setLogoImage(override);
      return undefined;
    }

    let isMounted = true;
    // Try company logo from company state
    const companyContext = getTemplateCompanyContext(null, companyState);
    const companyLogo = getCompanyLogoUrl(companyContext);
    if (companyLogo) {
      setLogoImage(companyLogo);
      return undefined;
    }

    // Fallback to the default premium logo S3 key
    resolvePresignedUrl(PREMIUM_LOGO_IMAGE_KEY)
      .then((url) => {
        if (isMounted && /^(https?:|data:|blob:)/i.test(String(url || '').trim())) {
          setLogoImage(String(url).trim());
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [sectionLogoUrl, companyState]);

  return logoImage;
}

function usePremiumMotion(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;

    const sections = Array.from(root.querySelectorAll('[data-awp-motion]'));
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    root.classList.add('awp-motion-ready');

    if (reduceMotion) {
      sections.forEach((section) => section.classList.add('is-visible'));
      return () => root.classList.remove('awp-motion-ready');
    }
    if (typeof IntersectionObserver === 'undefined') {
      sections.forEach((section) => section.classList.add('is-visible'));
      return () => root.classList.remove('awp-motion-ready');
    }

    const visibleSections = new Set();
    let frameId = 0;
    const updateMotion = () => {
      frameId = 0;
      const viewportHeight = Math.max(window.innerHeight, 1);
      const targets = visibleSections.size ? visibleSections : sections.slice(0, 1);
      targets.forEach((section) => {
        const rect = section.getBoundingClientRect();
        const progress = Math.max(0, Math.min(1, (viewportHeight - rect.top) / (viewportHeight + rect.height)));
        const shift = Math.max(-1, Math.min(1, ((rect.top + rect.height / 2) - viewportHeight / 2) / viewportHeight));
        section.style.setProperty('--awp-progress', progress.toFixed(4));
        section.style.setProperty('--awp-shift', shift.toFixed(4));
      });
    };
    const scheduleFrame = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateMotion);
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        entry.target.classList.toggle('is-visible', entry.isIntersecting);
        if (entry.isIntersecting) visibleSections.add(entry.target);
        else visibleSections.delete(entry.target);
      });
      scheduleFrame();
    }, { threshold: 0.12, rootMargin: '8% 0px -8% 0px' });
    sections.forEach((section) => observer.observe(section));

    const hero = root.querySelector('.awp-hero');
    const onHeroPointerMove = (event) => {
      const rect = hero.getBoundingClientRect();
      hero.style.setProperty('--awp-mx', (((event.clientX - rect.left) / rect.width) * 2 - 1).toFixed(3));
      hero.style.setProperty('--awp-my', (((event.clientY - rect.top) / rect.height) * 2 - 1).toFixed(3));
    };
    const resetHeroPointer = () => {
      hero.style.setProperty('--awp-mx', '0');
      hero.style.setProperty('--awp-my', '0');
    };
    hero?.addEventListener('pointermove', onHeroPointerMove, { passive: true });
    hero?.addEventListener('pointerleave', resetHeroPointer);

    const tiltCleanups = Array.from(root.querySelectorAll('[data-awp-tilt]')).map((card) => {
      const onMove = (event) => {
        const rect = card.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - 0.5;
        const y = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--awp-tilt-x', `${(-y * 7).toFixed(2)}deg`);
        card.style.setProperty('--awp-tilt-y', `${(x * 9).toFixed(2)}deg`);
      };
      const onLeave = () => {
        card.style.setProperty('--awp-tilt-x', '0deg');
        card.style.setProperty('--awp-tilt-y', '0deg');
      };
      card.addEventListener('pointermove', onMove, { passive: true });
      card.addEventListener('pointerleave', onLeave);
      return () => {
        card.removeEventListener('pointermove', onMove);
        card.removeEventListener('pointerleave', onLeave);
      };
    });

    window.addEventListener('scroll', scheduleFrame, { passive: true });
    window.addEventListener('resize', scheduleFrame);
    scheduleFrame();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', scheduleFrame);
      window.removeEventListener('resize', scheduleFrame);
      hero?.removeEventListener('pointermove', onHeroPointerMove);
      hero?.removeEventListener('pointerleave', resetHeroPointer);
      tiltCleanups.forEach((cleanup) => cleanup());
      if (frameId) window.cancelAnimationFrame(frameId);
      root.classList.remove('awp-motion-ready');
    };
  }, [rootRef]);
}

// ── Static content model (mirrors the original static HTML 1:1) ────────────

const DEFAULT_WHAT_SLIDES = [
  {
    key: 'discover',
    title: 'Discover',
    kicker: '01 / Discover',
    copy: 'We identify critical business rules, decision points and process gaps to create a clear implementation blueprint.',
    headline: 'Every decision earns its place.',
    note: 'Inside every workflow — a decade of R&D running at full capacity.',
  },
  {
    key: 'develop',
    title: 'Develop',
    kicker: '02 / Develop',
    copy: 'We transform concepts into working solutions with production-grade architecture, implementation and integration support.',
    headline: 'Every release earns its place.',
    note: 'Inside every delivery lane — precision engineering running at full capacity.',
  },
  {
    key: 'maintain',
    title: 'Maintain',
    kicker: '03 / Maintain',
    copy: 'We keep applications reliable and efficient through continuous support, tuning, issue resolution and lifecycle maintenance.',
    headline: 'Every application earns its place.',
    note: 'Inside every support cycle — sustained optimization running at full capacity.',
  },
  {
    key: 'mentor',
    title: 'Mentor',
    kicker: '04 / Mentor',
    copy: 'We upskill internal teams, transfer knowledge and make sure your organization can operate confidently on its own.',
    headline: 'Every team earns its place.',
    note: 'Inside every enablement journey — proven knowledge running at full capacity.',
  },
];

const DEFAULT_SERVICE_CARDS = [
  {
    glyph: '\u25CE',
    title: 'IBM Products Implementation',
    body: 'End-to-end managed application development consulting across business domains using different IBM products.',
  },
  {
    glyph: '\u25CC',
    title: 'Managed IT Services',
    body: 'Lower TCO and improve application efficiency with on-premises, hybrid cloud and multi-cloud delivery models.',
  },
  {
    glyph: '\u25B3',
    title: 'Cloud Migration',
    body: 'Create a holistic cloud strategy that connects business goals, applications and IT architecture together.',
  },
  {
    glyph: '\u25A3',
    title: 'Application Modernization',
    body: 'Modernize existing applications with current cloud, technology, infrastructure and security practices.',
  },
];

const DEFAULT_STATS = [
  { value: '10+', label: 'Business Domains' },
  { value: '20+', label: 'Years of IT Experience' },
  { value: '20+', label: 'Clients' },
  { value: '10+', label: 'Technologies' },
];

const DEFAULT_KRUMAGO_DOMAINS = [
  {
    num: '01',
    title: 'Education',
    body: 'Connect the complete learning journey with one operational view for administrators, instructors and learners.',
    features: ['Admissions & enrollment', 'Learning operations', 'People & campus workflows'],
  },
  {
    num: '02',
    title: 'Retail',
    body: 'Coordinate everyday commerce across products, customers and fulfillment with tools designed to scale.',
    features: ['Catalog & inventory', 'Orders & fulfillment', 'Customer engagement'],
  },
  {
    num: '03',
    title: 'Flex Office',
    body: 'Run flexible spaces with connected member, location and service experiences from a single control layer.',
    features: ['Bookings & access', 'Members & billing', 'Space utilization'],
  },
  {
    num: '04',
    title: 'WAAS',
    body: 'Build and manage professional websites with enterprise-grade templates, content management and deployment tools.',
    features: ['Website builder', 'Content management', 'Template customization'],
  },
];

/** Merge author-provided item array with defaults. Author items win; defaults fill gaps. */
function resolveItems(sectionItems, defaults) {
  if (Array.isArray(sectionItems) && sectionItems.length > 0) return sectionItems;
  return defaults;
}

function WhatSlideArt() {
  return (
    <svg viewBox="0 0 300 320" xmlns="http://www.w3.org/2000/svg">
      <rect width="300" height="320" fill="#e1e5e7" />
      <rect x="112" y="122" width="46" height="182" rx="8" fill="#d0d7dc" />
      <circle cx="136" cy="136" r="66" fill="#e9eef1" />
      <circle cx="136" cy="136" r="22" fill="#8e99a3" />
      <path d="M136 136L136 26" stroke="#d9e0e4" strokeWidth="24" strokeLinecap="round" />
      <path d="M136 136L42 82" stroke="#d9e0e4" strokeWidth="24" strokeLinecap="round" />
      <path d="M136 136L240 82" stroke="#d9e0e4" strokeWidth="24" strokeLinecap="round" />
      <rect x="78" y="98" width="52" height="52" rx="8" fill="#f8f9fa" stroke="#bbc4ca" />
    </svg>
  );
}

function WhatSideArt() {
  return (
    <svg viewBox="0 0 100 124" xmlns="http://www.w3.org/2000/svg">
      <rect width="100" height="124" fill="#e5e8ea" />
      <rect x="47" y="34" width="8" height="56" rx="4" fill="#cfd6da" />
      <path d="M51 42L24 56" stroke="#d8dfe3" strokeWidth="8" strokeLinecap="round" />
      <path d="M51 42L77 57" stroke="#d8dfe3" strokeWidth="8" strokeLinecap="round" />
    </svg>
  );
}

const PREMIUM_CSS = `
.avps-waas-premium { --awp-bg:#ebe9e1; position:relative; font-family:Inter,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; color:#34424d; background:var(--awp-bg); overflow-x:hidden; }
.avps-waas-premium * { box-sizing: border-box; }
.avps-waas-premium a { text-decoration: none; color: inherit; }
.avps-waas-premium .awp-wrap { width:min(1280px,calc(100% - 48px)); margin:0 auto; }
body:has(.avps-waas-premium) #waas-template-header,
.waas-template-preview:has(.avps-waas-premium) #waas-global-header { display:none !important; }

.avps-waas-premium .awp-site-nav { position:absolute; top:0; left:0; right:0; z-index:50; width:min(1480px,calc(100% - 80px)); height:96px; display:flex; align-items:center; justify-content:space-between; margin:0 auto; border:0; border-radius:0; background:transparent; box-shadow:none; }
.avps-waas-premium .awp-brand { width:190px; height:60px; display:flex; align-items:center; flex:0 0 auto; min-width:0; color:#15236f; font-weight:800; }
.avps-waas-premium .awp-brand img { width:100%; height:100%; display:block; object-fit:contain; object-position:left center; filter:contrast(1.08) saturate(1.08); }
.avps-waas-premium .awp-brand-mark { flex:0 0 auto; width:34px; height:34px; border-radius:50%; display:grid; place-items:center; background:#243039; color:#fff; font-size:13px; font-weight:800; }
.avps-waas-premium .awp-brand-name { overflow:hidden; color:#15236f; text-overflow:ellipsis; white-space:nowrap; }
.avps-waas-premium .awp-primary-nav { display:flex; align-items:center; justify-content:flex-end; gap:clamp(30px,3vw,56px); }
.avps-waas-premium .awp-primary-nav a { position:relative; padding:12px 0; color:#15236f; font-size:14px; font-weight:700; letter-spacing:.005em; white-space:nowrap; }
.avps-waas-premium .awp-primary-nav a::after { content:""; position:absolute; left:0; right:100%; bottom:5px; height:1px; background:#202d82; transition:right .28s ease; }
.avps-waas-premium .awp-primary-nav a:hover::after { right:0; }
.avps-waas-premium .awp-mobile-menu { display:none; position:relative; color:#202d82; font-size:12px; }
.avps-waas-premium .awp-mobile-menu summary { cursor:pointer; list-style:none; }
.avps-waas-premium .awp-mobile-menu summary::-webkit-details-marker { display:none; }
.avps-waas-premium .awp-mobile-menu summary::before { content:"☰"; margin-right:7px; }
.avps-waas-premium .awp-mobile-menu nav { position:absolute; top:30px; right:0; min-width:190px; display:grid; padding:12px; border:1px solid rgba(32,45,130,.1); background:#fffaf3; box-shadow:0 18px 44px rgba(32,45,130,.14); }
.avps-waas-premium .awp-mobile-menu nav a { padding:11px 12px; color:#15236f; }

.avps-waas-premium .awp-hero { min-height:720px; position:relative; display:grid; place-items:center; overflow:hidden; background:linear-gradient(180deg,#e3e0d6 0%,#d9d5c8 100%); }
.avps-waas-premium .awp-hero-photo { position:absolute; inset:0; z-index:0; background-size:cover; background-position:center; opacity:.22; filter:saturate(.85); }
.avps-waas-premium .awp-hero::before { content:""; position:absolute; inset:0; background:radial-gradient(circle at 18% 30%,rgba(255,255,255,.56),transparent 24%),radial-gradient(circle at 84% 20%,rgba(255,255,255,.4),transparent 22%),linear-gradient(90deg,rgba(224,221,211,.94),rgba(224,221,211,.46),rgba(224,221,211,.9)); z-index:1; }
.avps-waas-premium .awp-hero::after { content:""; position:absolute; inset:0; background-image: linear-gradient(rgba(61,71,74,.03) 1px, transparent 1px), linear-gradient(90deg, rgba(61,71,74,.03) 1px, transparent 1px); background-size:42px 42px; z-index:1; opacity:.34; }
.avps-waas-premium .awp-hero-content { position:relative; z-index:3; text-align:center; width:min(920px,94vw); }
.avps-waas-premium .awp-hero-kicker { font-size:12px; text-transform:uppercase; letter-spacing:.18em; color:#68726f; font-weight:800; }
.avps-waas-premium .awp-hero-title { margin:18px 0 0; font-size:clamp(46px,9vw,108px); line-height:.82; letter-spacing:-.07em; color:#fbfbf8; font-weight:300; text-shadow:0 6px 18px rgba(80,70,50,.12); }
.avps-waas-premium .awp-hero-title span { display:block; }
.avps-waas-premium .awp-hero-copy { max-width:470px; margin:22px auto 28px; font-size:14px; line-height:1.75; color:#6f7773; }
.avps-waas-premium .awp-hero-btn { display:inline-flex; align-items:center; height:44px; padding:0 22px; background:#596b58; color:#fff; border-radius:4px; font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:.12em; box-shadow:0 18px 36px rgba(65,80,62,.18); transition:.25s; }
.avps-waas-premium .awp-hero-btn:hover { transform:translateY(-3px); }
.avps-waas-premium .awp-hero-side-note { position:absolute; right:22px; bottom:18px; z-index:3; font-size:12px; letter-spacing:.12em; color:#4a5553; }

.avps-waas-premium .awp-offerings { background:var(--awp-bg); color:#34424d; padding:96px 0 110px; position:relative; overflow:hidden; }
.avps-waas-premium .awp-offerings::before { content:""; position:absolute; inset:0; background:none; }
.avps-waas-premium .awp-offerings > .awp-wrap { width:min(1480px,calc(100% - 48px)); }
.avps-waas-premium .awp-section-head { text-align:center; position:relative; z-index:2; margin-bottom:54px; }
.avps-waas-premium .awp-pill { display:inline-flex; align-items:center; gap:8px; padding:6px 12px; border-radius:999px; border:1px solid rgba(52,66,77,.16); font-size:12px; text-transform:uppercase; letter-spacing:.12em; color:#66736f; }
.avps-waas-premium .awp-section-head h2 { margin:18px auto 0; max-width:1040px; font-size:clamp(34px,5vw,58px); line-height:1.16; letter-spacing:-.05em; }
.avps-waas-premium .awp-section-head h2 em { font-style:normal; color:#e3c249; }
.avps-waas-premium .awp-services-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:18px; position:relative; z-index:2; }
.avps-waas-premium .awp-service-card { min-height:326px; border:1px solid rgba(52,66,77,.13); border-radius:16px; background:rgba(255,255,255,.34); padding:22px; display:flex; flex-direction:column; justify-content:space-between; box-shadow:inset 0 1px 0 rgba(255,255,255,.55); transition:.28s; }
.avps-waas-premium .awp-service-card:hover { transform:translateY(-8px); border-color:rgba(154,119,19,.28); box-shadow:0 24px 58px rgba(52,66,77,.14); }
.avps-waas-premium .awp-service-icon { width:46px; height:46px; border-radius:50%; display:grid; place-items:center; color:#e3c249; background:rgba(227,194,73,.08); font-weight:800; font-size:18px; }
.avps-waas-premium .awp-service-card h3 { margin:46px 0 12px; font-size:22px; line-height:1.15; letter-spacing:-.03em; }
.avps-waas-premium .awp-service-card p { margin:0; color:#66736f; font-size:14px; line-height:1.72; }
.avps-waas-premium .awp-service-link { margin-top:20px; display:inline-flex; align-items:center; gap:8px; font-size:13px; font-weight:800; color:#8b6a08; }
.avps-waas-premium .awp-service-link span { width:20px; height:20px; border-radius:50%; display:grid; place-items:center; background:#fff; color:#111; }

.avps-waas-premium .awp-what { background:var(--awp-bg); padding:104px 0 116px; position:relative; overflow:hidden; }
.avps-waas-premium .awp-what::before { content:""; position:absolute; width:560px; height:560px; border-radius:50%; right:-220px; top:-240px; background:none; pointer-events:none; }
.avps-waas-premium .awp-what > .awp-wrap { width:min(1480px,calc(100% - 48px)); margin:0 auto; }
.avps-waas-premium .awp-what-intro { display:grid; grid-template-columns:minmax(0,1fr) minmax(280px,440px); gap:64px; align-items:end; margin-bottom:42px; }
.avps-waas-premium .awp-what-eyebrow { display:flex; align-items:center; gap:10px; margin-bottom:16px; color:#5f6d74; font-size:11px; font-weight:800; letter-spacing:.16em; text-transform:uppercase; }
.avps-waas-premium .awp-what-eyebrow::before { content:""; width:32px; height:1px; background:#e0b93e; }
.avps-waas-premium .awp-what-intro h2 { max-width:760px; margin:0; color:#273640; font-size:clamp(42px,5vw,72px); line-height:.96; letter-spacing:-.065em; font-weight:650; }
.avps-waas-premium .awp-what-intro > p { margin:0 0 5px; color:#68757b; font-size:15px; line-height:1.75; }
.avps-waas-premium .awp-what-shell { position:relative; display:grid; grid-template-columns:minmax(0,1.42fr) minmax(330px,.58fr); grid-template-rows:auto 1fr; gap:0 28px; min-height:620px; padding:20px; background:rgba(250,250,247,.9); border:1px solid rgba(69,80,88,.13); border-radius:30px; box-shadow:0 32px 90px rgba(33,41,46,.13); overflow:hidden; }
.avps-waas-premium .awp-what-nav { grid-column:1/-1; display:flex; gap:8px; padding:0 0 18px; overflow-x:auto; scrollbar-width:none; }
.avps-waas-premium .awp-what-nav::-webkit-scrollbar { display:none; }
.avps-waas-premium .awp-step-tab { appearance:none; border:1px solid transparent; border-radius:999px; background:transparent; color:#69767d; padding:10px 15px; font:inherit; font-size:11px; font-weight:800; letter-spacing:.11em; text-transform:uppercase; cursor:pointer; white-space:nowrap; transition:background .22s ease,color .22s ease,border-color .22s ease,box-shadow .22s ease; }
.avps-waas-premium .awp-step-tab:hover { color:#273640; background:#eef0ed; }
.avps-waas-premium .awp-step-tab.active { color:#fff; background:#273640; border-color:#273640; box-shadow:0 10px 24px rgba(39,54,64,.18); }
.avps-waas-premium .awp-what-stage { position:relative; min-height:540px; overflow:hidden; border-radius:22px; background:#dfe4e5; }
.avps-waas-premium .awp-slide-wrap { position:absolute; inset:0; }
.avps-waas-premium .awp-slide { position:absolute; inset:0; opacity:0; transition:opacity .7s ease; pointer-events:none; }
.avps-waas-premium .awp-slide.active { opacity:1; pointer-events:auto; }
.avps-waas-premium .awp-slide::after { content:""; position:absolute; inset:0; background:linear-gradient(180deg,transparent 48%,rgba(6,13,18,.68) 100%); pointer-events:none; }
.avps-waas-premium .awp-main-card { position:absolute; inset:0; background:#dfe3e5; overflow:hidden; }
.avps-waas-premium .awp-side-card { position:absolute; z-index:2; right:20px; width:112px; height:92px; border:3px solid rgba(255,255,255,.9); border-radius:14px; background:#e1e4e6; box-shadow:0 14px 30px rgba(18,29,36,.2); overflow:hidden; }
.avps-waas-premium .awp-side-card.s1 { top:20px; }
.avps-waas-premium .awp-side-card.s2 { top:126px; }
.avps-waas-premium .awp-side-card.s3 { top:232px; }
.avps-waas-premium .awp-asset { width:100%; height:100%; background-size:cover; background-position:center; filter:saturate(.88) contrast(.96); }
.avps-waas-premium .awp-asset svg { display:none; }
.avps-waas-premium .awp-slide:nth-child(1) .awp-main-card .awp-asset { background-image:url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=900&q=80'); }
.avps-waas-premium .awp-slide:nth-child(1) .awp-side-card.s1 .awp-asset { background-image:url('https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(1) .awp-side-card.s2 .awp-asset { background-image:url('https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(1) .awp-side-card.s3 .awp-asset { background-image:url('https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=320&q=80'); }
.avps-waas-premium .awp-slide:nth-child(2) .awp-main-card .awp-asset { background-image:url('https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=900&q=80'); }
.avps-waas-premium .awp-slide:nth-child(2) .awp-side-card.s1 .awp-asset { background-image:url('https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(2) .awp-side-card.s2 .awp-asset { background-image:url('https://images.unsplash.com/photo-1551434678-e076c223a692?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(2) .awp-side-card.s3 .awp-asset { background-image:url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=320&q=80'); }
.avps-waas-premium .awp-slide:nth-child(3) .awp-main-card .awp-asset { background-image:url('https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=900&q=80'); }
.avps-waas-premium .awp-slide:nth-child(3) .awp-side-card.s1 .awp-asset { background-image:url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(3) .awp-side-card.s2 .awp-asset { background-image:url('https://images.unsplash.com/photo-1497215728101-856f4ea42174?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(3) .awp-side-card.s3 .awp-asset { background-image:url('https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=320&q=80'); }
.avps-waas-premium .awp-slide:nth-child(4) .awp-main-card .awp-asset { background-image:url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=900&q=80'); }
.avps-waas-premium .awp-slide:nth-child(4) .awp-side-card.s1 .awp-asset { background-image:url('https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(4) .awp-side-card.s2 .awp-asset { background-image:url('https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=420&q=80'); }
.avps-waas-premium .awp-slide:nth-child(4) .awp-side-card.s3 .awp-asset { background-image:url('https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=320&q=80'); }
.avps-waas-premium .awp-visual-caption { position:absolute; left:28px; right:160px; bottom:26px; z-index:3; color:#fff; }
.avps-waas-premium .awp-visual-caption span { display:block; margin-bottom:9px; color:rgba(255,255,255,.7); font-size:10px; font-weight:800; letter-spacing:.15em; text-transform:uppercase; }
.avps-waas-premium .awp-visual-caption strong { display:block; max-width:560px; font-size:clamp(30px,3.4vw,48px); line-height:1; letter-spacing:-.055em; font-weight:650; }
.avps-waas-premium .awp-desc-panel { align-self:stretch; display:flex; flex-direction:column; justify-content:center; padding:40px 34px; border-radius:22px; background:#0b141c; color:#fff; box-shadow:inset 0 1px 0 rgba(255,255,255,.06); }
.avps-waas-premium .awp-what-kicker { font-size:11px; letter-spacing:.14em; text-transform:uppercase; color:#718088; font-weight:800; }
.avps-waas-premium .awp-what-title { margin:18px 0 14px; font-size:clamp(32px,3vw,44px); line-height:1; letter-spacing:-.055em; color:#fff; font-weight:650; }
.avps-waas-premium .awp-what-text { margin:0; color:#b8c2c9; font-size:14px; line-height:1.75; }
.avps-waas-premium .awp-bottom-note { margin-top:26px; padding:18px 0 0; border-top:1px solid rgba(255,255,255,.1); color:#d9bd56; font-size:12px; line-height:1.55; }
.avps-waas-premium .awp-what-action { align-self:flex-start; display:inline-flex; align-items:center; gap:10px; margin-top:28px; color:#fff; font-size:11px; font-weight:800; letter-spacing:.12em; text-transform:uppercase; }
.avps-waas-premium .awp-what-action span { display:grid; place-items:center; width:28px; height:28px; border-radius:50%; background:#e3c249; color:#0b141c; transition:transform .2s ease; }
.avps-waas-premium .awp-what-action:hover span { transform:translate(2px,-2px); }
.avps-waas-premium .awp-dots { display:flex; gap:8px; margin-top:34px; }
.avps-waas-premium .awp-dot { width:8px; height:8px; border-radius:50%; border:0; background:#53606a; cursor:pointer; transition:.2s; padding:0; }
.avps-waas-premium .awp-dot.active { width:30px; border-radius:999px; background:#e3c249; }

.avps-waas-premium .awp-krumago { background:var(--awp-bg); overflow:hidden; }
.avps-waas-premium .awp-krumago-hero { position:relative; min-height:980px; background:var(--awp-bg); overflow:hidden; }
.avps-waas-premium .awp-krumago-hero::before { content:""; position:absolute; inset:0; background:none; z-index:1; }
.avps-waas-premium .awp-krumago-content { position:relative; z-index:3; width:min(1580px,calc(100% - 48px)); margin:0 auto; padding:110px 0 70px; min-height:980px; display:flex; flex-direction:column; justify-content:flex-start; gap:42px; }
.avps-waas-premium .awp-krumago-top { display:grid; grid-template-columns:1fr auto; gap:48px; align-items:end; }
.avps-waas-premium .awp-krumago-logo { font-size:clamp(58px,8vw,112px); line-height:.82; letter-spacing:-.08em; font-weight:950; margin:0 0 22px; text-shadow:0 18px 34px rgba(0,0,0,.28); }
.avps-waas-premium .awp-krumago-logo .kru { color:#0516b8; }
.avps-waas-premium .awp-krumago-logo .ma { color:#f0000b; }
.avps-waas-premium .awp-krumago-logo .go { color:#0516b8; }
.avps-waas-premium .awp-krumago-title { font-size:clamp(42px,6vw,82px); line-height:.95; letter-spacing:-.075em; color:#273640; margin:0 0 18px; font-weight:800; text-shadow:none; }
.avps-waas-premium .awp-krumago-sub { max-width:640px; color:#66736f; font-size:17px; line-height:1.7; margin:0; }
.avps-waas-premium .awp-krumago-stat { justify-self:end; align-self:end; border-radius:28px; background:rgba(255,255,255,.38); border:1px solid rgba(52,66,77,.13); backdrop-filter:blur(14px); padding:28px 30px; box-shadow:0 24px 52px rgba(52,66,77,.1); text-align:left; min-width:320px; }
.avps-waas-premium .awp-krumago-stat strong { display:block; color:#0b63ff; font-size:96px; line-height:.8; letter-spacing:-.09em; font-weight:500; text-shadow:0 12px 28px rgba(0,0,0,.18); }
.avps-waas-premium .awp-krumago-stat span { display:block; margin-top:18px; font-size:15px; max-width:250px; line-height:1.4; color:#34424d; }
.avps-waas-premium .awp-krumago-panel { width:100%; border-radius:34px; background:rgba(255,255,255,.34); border:1px solid rgba(52,66,77,.13); backdrop-filter:blur(18px); box-shadow:0 30px 70px rgba(52,66,77,.12); padding:clamp(24px,3vw,42px); margin-top:18px; }
.avps-waas-premium .awp-krumago-domains { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:16px; width:100%; }
.avps-waas-premium .awp-krumago-domain { min-height:330px; border-radius:22px; background:#172127; color:#fff; padding:28px; border:1px solid rgba(255,255,255,.08); box-shadow:0 20px 40px rgba(52,66,77,.13); display:flex; flex-direction:column; position:relative; overflow:hidden; }
.avps-waas-premium .awp-krumago-domain::after { content:""; position:absolute; width:150px; height:150px; right:-76px; top:-76px; border:1px solid rgba(255,255,255,.08); border-radius:50%; box-shadow:0 0 0 34px rgba(255,255,255,.018); }
.avps-waas-premium .awp-domain-number { display:flex; align-items:center; justify-content:space-between; gap:16px; color:#ff784e; font-size:12px; letter-spacing:.12em; font-weight:900; margin-bottom:42px; }
.avps-waas-premium .awp-domain-number small { color:#7f8d95; font-size:9px; letter-spacing:.16em; text-transform:uppercase; }
.avps-waas-premium .awp-krumago-domain b { display:block; font-size:30px; letter-spacing:-.045em; margin-bottom:12px; }
.avps-waas-premium .awp-krumago-domain > p { margin:0; color:#b7c1c6; font-size:14px; line-height:1.65; }
.avps-waas-premium .awp-domain-features { display:grid; gap:9px; margin:28px 0 0; padding:22px 0 0; border-top:1px solid rgba(255,255,255,.1); list-style:none; }
.avps-waas-premium .awp-domain-features li { display:flex; align-items:center; gap:10px; color:#eef1f2; font-size:12px; font-weight:700; }
.avps-waas-premium .awp-domain-features li::before { content:""; width:6px; height:6px; flex:0 0 auto; border-radius:50%; background:#e3c249; box-shadow:0 0 0 4px rgba(227,194,73,.09); }
.avps-waas-premium .awp-krumago-copy { display:grid; grid-template-columns:minmax(180px,.48fr) minmax(0,1.52fr); gap:clamp(28px,5vw,76px); align-items:end; margin-bottom:34px; padding:0 4px; }
.avps-waas-premium .awp-krumago-tags { display:flex; gap:8px; margin:0; flex-wrap:wrap; align-self:start; }
.avps-waas-premium .awp-krumago-tags span { font-size:10px; border-radius:999px; background:rgba(255,255,255,.82); color:#101010; padding:6px 9px; font-weight:700; }
.avps-waas-premium .awp-krumago-tags span:nth-child(2) { background:rgba(255,255,255,.82); color:#101010; }
.avps-waas-premium .awp-krumago-headline { font-size:clamp(38px,4.5vw,64px); line-height:.96; letter-spacing:-.07em; margin:0; color:#273640; font-weight:500; max-width:920px; text-shadow:none; }

.avps-waas-premium .awp-who { background:var(--awp-bg); padding:110px 0; position:relative; overflow:hidden; }
.avps-waas-premium .awp-who::before { content:""; position:absolute; inset:0; background:none; pointer-events:none; }
.avps-waas-premium .awp-who-grid { position:relative; z-index:1; width:calc(100% - 48px); max-width:none; display:grid; grid-template-columns:minmax(420px,.75fr) minmax(0,1.25fr); gap:clamp(56px,7vw,120px); align-items:center; }
.avps-waas-premium .awp-who h2 { margin:0; font-size:clamp(54px,7vw,110px); line-height:.84; letter-spacing:-.08em; font-weight:300; color:#35434b; }
.avps-waas-premium .awp-who p { max-width:680px; color:#69716d; font-size:15px; line-height:1.8; margin:18px 0 0; }
.avps-waas-premium .awp-stats { display:grid; grid-template-columns:repeat(4,1fr); gap:0; border-top:1px solid rgba(61,71,69,.16); }
.avps-waas-premium .awp-stat { min-height:240px; border-left:1px solid rgba(61,71,69,.16); padding:48px 40px; display:flex; flex-direction:column; justify-content:flex-start; position:relative; }
.avps-waas-premium .awp-stat:first-child { border-left:none; }
.avps-waas-premium .awp-stat strong { font-size:clamp(42px,5vw,64px); line-height:1; letter-spacing:-.06em; color:#202D82; margin-bottom:16px; display:block; }
.avps-waas-premium .awp-stat span { display:block; color:#35434b; font-weight:700; font-size:18px; }

@keyframes awpHeroIn { from { opacity:0; transform:translateY(38px); filter:blur(6px); } to { opacity:1; transform:translateY(0); filter:blur(0); } }

/* Premium motion system: every section has its own depth language. */
.avps-waas-premium.awp-motion-ready [data-awp-motion] { --awp-progress:0; --awp-shift:0; }
.avps-waas-premium.awp-motion-ready .awp-hero { perspective:1200px; transform-style:preserve-3d; }
.avps-waas-premium.awp-motion-ready .awp-hero-content { animation:none; opacity:0; filter:blur(7px); transform:translate3d(0,44px,40px) rotateX(5deg); transition:opacity .9s ease,filter .9s ease,transform .9s cubic-bezier(.18,.8,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-hero.is-visible .awp-hero-content { opacity:1; filter:blur(0); transform:translate3d(calc(var(--awp-mx,0) * 12px),calc(var(--awp-shift) * -34px),70px) rotateX(calc(var(--awp-my,0) * -2deg)) rotateY(calc(var(--awp-mx,0) * 2deg)); }

.avps-waas-premium.awp-motion-ready .awp-offerings .awp-section-head { opacity:0; transform:translateY(42px) scale(.97); transition:opacity .75s ease,transform .85s cubic-bezier(.18,.8,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-offerings.is-visible .awp-section-head { opacity:1; transform:translateY(calc(var(--awp-shift) * -10px)) scale(1); }
.avps-waas-premium.awp-motion-ready .awp-services-grid { perspective:1100px; transform-style:preserve-3d; }
.avps-waas-premium.awp-motion-ready .awp-service-card { --awp-tilt-x:0deg; --awp-tilt-y:0deg; opacity:0; transform:translate3d(0,80px,-130px) rotateY(var(--awp-entry-rotate)); transition:opacity .7s ease,transform .75s cubic-bezier(.18,.8,.2,1),border-color .28s,box-shadow .28s; transition-delay:var(--awp-delay); will-change:transform; }
.avps-waas-premium.awp-motion-ready .awp-offerings.is-visible .awp-service-card { opacity:1; transform:translate3d(0,calc(var(--awp-shift) * -12px),0) rotateX(var(--awp-tilt-x)) rotateY(var(--awp-tilt-y)); }
.avps-waas-premium.awp-motion-ready .awp-offerings.is-visible .awp-service-card:hover { transform:translate3d(0,-12px,34px) rotateX(var(--awp-tilt-x)) rotateY(var(--awp-tilt-y)); }

.avps-waas-premium.awp-motion-ready .awp-what-intro > * { opacity:0; transform:translateY(38px); transition:opacity .72s ease,transform .8s cubic-bezier(.18,.8,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-what-intro > p { transition-delay:120ms; }
.avps-waas-premium.awp-motion-ready .awp-what.is-visible .awp-what-intro > * { opacity:1; transform:translateY(0); }
.avps-waas-premium.awp-motion-ready .awp-what { perspective:1400px; }
.avps-waas-premium.awp-motion-ready .awp-what-shell { opacity:0; transform-origin:50% 18%; transform:translateY(70px) scale(.92) rotateX(9deg); transition:opacity .8s ease,transform 1s cubic-bezier(.16,.84,.2,1); will-change:transform; }
.avps-waas-premium.awp-motion-ready .awp-what.is-visible .awp-what-shell { opacity:1; transform:translateY(calc(var(--awp-shift) * -18px)) scale(1) rotateX(calc(var(--awp-shift) * -1.2deg)); }
.avps-waas-premium.awp-motion-ready .awp-side-card { transition:opacity .5s ease,transform .65s cubic-bezier(.18,.8,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-side-card.s1 { transform:translate3d(calc(var(--awp-shift) * -7px),calc(var(--awp-shift) * -18px),42px); }
.avps-waas-premium.awp-motion-ready .awp-side-card.s2 { transform:translate3d(calc(var(--awp-shift) * 7px),calc(var(--awp-shift) * -10px),24px); }
.avps-waas-premium.awp-motion-ready .awp-side-card.s3 { transform:translate3d(calc(var(--awp-shift) * -4px),calc(var(--awp-shift) * -5px),10px); }

.avps-waas-premium.awp-motion-ready .awp-krumago-hero { perspective:1500px; }
.avps-waas-premium.awp-motion-ready .awp-krumago-top { opacity:0; transform:translate3d(0,70px,-80px); transition:opacity .8s ease,transform .95s cubic-bezier(.16,.84,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-krumago.is-visible .awp-krumago-top { opacity:1; transform:translate3d(0,calc(var(--awp-shift) * -20px),20px); }
.avps-waas-premium.awp-motion-ready .awp-krumago-panel { opacity:0; transform-origin:50% 100%; transform:translate3d(0,110px,-100px) rotateX(10deg); transition:opacity .85s ease,transform 1.05s cubic-bezier(.16,.84,.2,1); will-change:transform; }
.avps-waas-premium.awp-motion-ready .awp-krumago.is-visible .awp-krumago-panel { opacity:1; transform:translate3d(0,calc(var(--awp-shift) * -24px),30px) rotateX(calc(var(--awp-shift) * -1.4deg)); }
.avps-waas-premium.awp-motion-ready .awp-krumago-domains > div { opacity:0; transform:translateX(-42px) rotateY(-8deg); transition:opacity .55s ease,transform .7s cubic-bezier(.18,.8,.2,1); transition-delay:var(--awp-delay); }
.avps-waas-premium.awp-motion-ready .awp-krumago.is-visible .awp-krumago-domains > div { opacity:1; transform:translateX(0) rotateY(0); }

.avps-waas-premium.awp-motion-ready .awp-who-copy { opacity:0; transform:translateX(-70px) rotateY(7deg); transition:opacity .75s ease,transform .9s cubic-bezier(.18,.8,.2,1); }
.avps-waas-premium.awp-motion-ready .awp-who.is-visible .awp-who-copy { opacity:1; transform:translateX(calc(var(--awp-shift) * 8px)) rotateY(0); }
.avps-waas-premium.awp-motion-ready .awp-stat { opacity:0; transform:translate3d(54px,38px,-80px); transition:opacity .62s ease,transform .78s cubic-bezier(.18,.8,.2,1); transition-delay:var(--awp-delay); }
.avps-waas-premium.awp-motion-ready .awp-who.is-visible .awp-stat { opacity:1; transform:translate3d(0,calc(var(--awp-shift) * -10px),0); }

.avps-waas-premium.awp-motion-ready .awp-footer-main > * { opacity:0; transform:translateY(34px); transition:opacity .6s ease,transform .72s cubic-bezier(.18,.8,.2,1); transition-delay:var(--awp-delay); }
.avps-waas-premium.awp-motion-ready .awp-footer.is-visible .awp-footer-main > * { opacity:1; transform:translateY(0); }
.avps-waas-premium.awp-motion-ready .awp-footer::before { transform:translate3d(calc(var(--awp-shift) * -24px),calc(var(--awp-shift) * 18px),0) rotate(calc(var(--awp-shift) * 5deg)); transition:transform .12s linear; }

@media (prefers-reduced-motion: reduce) {
  .avps-waas-premium.awp-motion-ready *,
  .avps-waas-premium.awp-motion-ready *::before,
  .avps-waas-premium.awp-motion-ready *::after { animation:none !important; transition-duration:.01ms !important; scroll-behavior:auto !important; }
  .avps-waas-premium.awp-motion-ready [data-awp-motion] *,
  .avps-waas-premium.awp-motion-ready [data-awp-motion] { opacity:1 !important; filter:none !important; transform:none !important; }
}

@media (max-width: 1100px) {
  .avps-waas-premium .awp-services-grid { grid-template-columns:repeat(2,1fr); }
  .avps-waas-premium .awp-what-shell { grid-template-columns:minmax(0,1.2fr) minmax(300px,.8fr); }
  .avps-waas-premium .awp-desc-panel { padding:34px 28px; }
}
@media (max-width: 820px) {
  .avps-waas-premium .awp-site-nav { width:calc(100% - 32px); height:72px; }
  .avps-waas-premium .awp-primary-nav { display:none; }
  .avps-waas-premium .awp-mobile-menu { display:block; }
  .avps-waas-premium .awp-hero { min-height:600px; }
  .avps-waas-premium .awp-hero-title { font-size:60px; }
  .avps-waas-premium .awp-what-intro { grid-template-columns:1fr; gap:20px; margin-bottom:30px; }
  .avps-waas-premium .awp-what-intro > p { max-width:620px; }
  .avps-waas-premium .awp-what-shell { grid-template-columns:1fr; grid-template-rows:auto 460px auto; gap:18px; min-height:0; }
  .avps-waas-premium .awp-what-nav { padding-bottom:0; }
  .avps-waas-premium .awp-what-stage { min-height:460px; }
  .avps-waas-premium .awp-desc-panel { min-height:380px; }
  .avps-waas-premium .awp-who-grid { grid-template-columns:1fr; gap:26px; }
  .avps-waas-premium .awp-krumago-top,
  .avps-waas-premium .awp-krumago-copy { grid-template-columns:1fr; }
  .avps-waas-premium .awp-krumago-domains { grid-template-columns:1fr; }
  .avps-waas-premium .awp-krumago-stat { justify-self:start; }
  .avps-waas-premium .awp-krumago-hero,
  .avps-waas-premium .awp-krumago-content { min-height:1120px; }
}
@media (max-width: 560px) {
  .avps-waas-premium .awp-wrap { width:min(100% - 24px,1180px); }
  .avps-waas-premium .awp-brand { width:112px; height:38px; }
  .avps-waas-premium .awp-brand-name { font-size:13px; }
  .avps-waas-premium .awp-hero-title { font-size:46px; }
  .avps-waas-premium .awp-services-grid { grid-template-columns:1fr; }
  .avps-waas-premium .awp-stats { grid-template-columns:1fr; }
  .avps-waas-premium .awp-what { padding:76px 0 82px; }
  .avps-waas-premium .awp-what > .awp-wrap { width:min(100% - 20px,1480px); }
  .avps-waas-premium .awp-what-intro h2 { font-size:42px; }
  .avps-waas-premium .awp-what-shell { grid-template-rows:auto 390px auto; padding:12px; border-radius:22px; }
  .avps-waas-premium .awp-what-stage { min-height:390px; border-radius:16px; }
  .avps-waas-premium .awp-side-card { right:12px; width:76px; height:64px; border-width:2px; border-radius:10px; }
  .avps-waas-premium .awp-side-card.s1 { top:12px; }
  .avps-waas-premium .awp-side-card.s2 { top:86px; }
  .avps-waas-premium .awp-side-card.s3 { top:160px; }
  .avps-waas-premium .awp-visual-caption { left:20px; right:98px; bottom:20px; }
  .avps-waas-premium .awp-visual-caption strong { font-size:30px; }
  .avps-waas-premium .awp-desc-panel { min-height:360px; padding:32px 24px; border-radius:16px; }
  .avps-waas-premium .awp-krumago-hero,
  .avps-waas-premium .awp-krumago-content { min-height:1280px; }
  .avps-waas-premium .awp-krumago-content { padding-top:94px; }
  .avps-waas-premium .awp-krumago-panel { padding:22px; border-radius:26px; }
  .avps-waas-premium .awp-krumago-logo { font-size:64px; }
  .avps-waas-premium .awp-krumago-title { font-size:48px; }
}
.avps-waas-premium .awp-footer { position:relative; overflow:hidden; padding:88px 0 28px; color:#34424d; background:var(--awp-bg); }
.avps-waas-premium .awp-footer::before { content:""; position:absolute; width:430px; height:430px; right:-180px; top:-220px; border:1px solid rgba(52,66,77,.1); border-radius:50%; box-shadow:0 0 0 70px rgba(52,66,77,.025),0 0 0 140px rgba(52,66,77,.015); }
.avps-waas-premium .awp-footer-main { position:relative; z-index:2; display:grid; grid-template-columns:1.35fr .65fr .72fr .9fr; gap:clamp(34px,5vw,72px); padding-bottom:68px; }
.avps-waas-premium .awp-footer-brand { display:flex; align-items:center; gap:12px; color:#273640; font-size:18px; font-weight:850; }
.avps-waas-premium .awp-footer-brand .awp-brand-mark { background:#273640; color:#fff; }
.avps-waas-premium .awp-footer-intro { max-width:390px; margin:24px 0 0; color:#66736f; font-size:14px; line-height:1.8; }
.avps-waas-premium .awp-footer h3 { margin:0 0 22px; color:#273640; font-size:12px; font-weight:900; letter-spacing:.13em; text-transform:uppercase; }
.avps-waas-premium .awp-footer-links { display:grid; gap:14px; align-content:start; }
.avps-waas-premium .awp-footer-links a { width:fit-content; color:#66736f; font-size:14px; transition:color .25s,transform .25s; }
.avps-waas-premium .awp-footer-links a:hover { color:#8b6a08; transform:translateX(4px); }
.avps-waas-premium .awp-footer-contact { display:grid; gap:14px; color:#66736f; font-size:14px; line-height:1.6; }
.avps-waas-premium .awp-footer-contact a { color:#273640; font-weight:750; }
.avps-waas-premium .awp-footer-bottom { position:relative; z-index:2; display:flex; align-items:center; justify-content:space-between; gap:24px; padding-top:26px; border-top:1px solid rgba(52,66,77,.13); color:#71818b; font-size:12px; }
.avps-waas-premium .awp-footer-bottom-links { display:flex; flex-wrap:wrap; gap:20px; }
@media (max-width:900px) {
  .avps-waas-premium .awp-footer-main { grid-template-columns:1fr 1fr; }
}
@media (max-width:560px) {
  .avps-waas-premium .awp-footer { padding-top:68px; }
  .avps-waas-premium .awp-footer-main { grid-template-columns:1fr; padding-bottom:46px; }
  .avps-waas-premium .awp-footer-bottom { align-items:flex-start; flex-direction:column; }
}
`;

function PremiumHeader({ content, theme }) {
  const brandName = content.brandName || theme.brandName || 'AVPS Technologies';
  const logoImage = usePremiumLogoImage(content.logoUrl || content.logoImageUrl);

  const navLinks = Array.isArray(content.navLinks) && content.navLinks.length > 0
    ? content.navLinks
    : [
      { label: 'Home', route: '/' },
      { label: 'About', route: '/about' },
      { label: 'Services', route: '/services' },
      { label: 'Careers', route: '/careers' },
      { label: 'Contact us', route: '/contactus' },
    ];

  return (
    <header className="awp-site-nav">
      <a className="awp-brand" href="/" aria-label={`${brandName} home`}>
        {logoImage ? <img src={logoImage} alt={brandName} /> : <span className="awp-brand-name">{brandName}</span>}
      </a>
      <nav className="awp-primary-nav" aria-label="Premium home navigation">
        {navLinks.map((link) => (
          <a key={link.route || link.label} href={link.route || '/'}>{link.label}</a>
        ))}
      </nav>
      <details className="awp-mobile-menu">
        <summary>Menu</summary>
        <nav>
          {navLinks.map((link) => (
            <a key={link.route || link.label} href={link.route || '/'}>{link.label}</a>
          ))}
        </nav>
      </details>
    </header>
  );
}

function PremiumHero({ content, theme }) {
  const heroImage = usePremiumHeroImage(
    content.heroImageUrl
    || content.heroImageContent?.displayLink
    || content.heroImageContent?.storedLink
    || content.backgroundImage
    || content.backgroundImageContent?.displayLink
  );

  const titleLine1 = content.heroTitleLine1 || content.titleLine1 || 'Technology that';
  const titleLine2 = content.heroTitleLine2 || content.titleLine2 || 'moves minds';
  const heroCopy = content.heroCopy || content.subtitle || '\u201cWe shape reliable digital journeys where people, process and technology move together with clarity.\u201d';
  const heroCta = content.heroCtaLabel || content.ctaLabel || 'Explore Services';
  const heroCtaUrl = content.heroCtaUrl || content.ctaUrl || '#awp-offerings';
  const sideNote = content.heroSideNote || 'ENGINEERED FOR GROWTH';

  return (
    <section className="awp-hero" id="awp-home" data-awp-motion="hero">
      <div
        className="awp-hero-photo"
        style={heroImage ? { backgroundImage: `url(${heroImage})` } : undefined}
        aria-hidden="true"
      />
      <div className="awp-hero-content">
        <div className="awp-hero-kicker">{content.brandName || theme.brandName || 'AVPS Technologies'}</div>
        <h1 className="awp-hero-title">
          <span>{titleLine1}</span>
          <span>{titleLine2}</span>
        </h1>
        <p className="awp-hero-copy">{heroCopy}</p>
        <a className="awp-hero-btn" href={heroCtaUrl}>{heroCta}</a>
      </div>
      <div className="awp-hero-side-note">{sideNote}</div>
    </section>
  );
}

function PremiumOfferings({ content }) {
  const offeringsTitle = content.offeringsTitle || 'We do not just build solutions,';
  const offeringsAccent = content.offeringsAccent || 'enterprise experiences';
  const offeringsPrefix = content.offeringsPrefix || 'we craft';
  const offeringsEyebrow = content.offeringsEyebrow || 'Services';
  const serviceCards = resolveItems(content.services, DEFAULT_SERVICE_CARDS);

  return (
    <section className="awp-offerings" id="awp-offerings" data-awp-motion="services">
      <div className="awp-wrap">
        <div className="awp-section-head">
          <div className="awp-pill">&#10022; {offeringsEyebrow}</div>
          <h2>
            <span style={{ color: content.offeringsColor || '#24399A' }}>{offeringsTitle}<br />
            {offeringsPrefix}</span> <em>{offeringsAccent}</em>
          </h2>
        </div>
        <div className="awp-services-grid">
          {serviceCards.map((card, index) => (
            <article className="awp-service-card" data-awp-tilt key={card.title || index} style={{ '--awp-entry-rotate': `${(index - 1.5) * 7}deg`, '--awp-delay': `${index * 90}ms` }}>
              <div>
                <div className="awp-service-icon">{card.glyph || card.icon || '\u25CE'}</div>
                <h3>{card.title}</h3>
                <p>{card.body || card.description}</p>
              </div>
              <a className="awp-service-link" href={card.url || '#awp-offerings'}>
                {card.ctaLabel || 'Start a Project'} <span>&#8599;</span>
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function PremiumWhat({ content }) {
  const slides = resolveItems(content.slides, DEFAULT_WHAT_SLIDES);

  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    const restart = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCurrent((prev) => (prev + 1) % slides.length);
      }, 5000);
    };
    restart();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [slides.length]);

  const goTo = (index) => {
    setCurrent(((index % slides.length) + slides.length) % slides.length);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 5000);
  };

  const active = slides[current] || slides[0];

  const whatEyebrow = content.whatEyebrow || 'How we work';
  const whatTitle = content.whatTitle || 'From first question to lasting capability.';
  const whatSubtitle = content.whatSubtitle || 'A practical delivery rhythm that turns complex requirements into dependable systems, then equips your team to keep moving forward.';
  const frameworkLabel = content.frameworkLabel || 'AVPS delivery framework';

  return (
    <section className="awp-what" id="awp-what" data-awp-motion="process">
      <div className="awp-wrap">
        <div className="awp-what-intro">
          <div>
            <div className="awp-what-eyebrow">{whatEyebrow}</div>
            <h2>{whatTitle}</h2>
          </div>
          <p>{whatSubtitle}</p>
        </div>

        <div className="awp-what-shell">
          <nav className="awp-what-nav">
            {slides.map((slide, index) => (
              <button
                type="button"
                key={slide.key || index}
                className={`awp-step-tab${index === current ? ' active' : ''}`}
                aria-pressed={index === current}
                onClick={() => goTo(index)}
              >
                {String(index + 1).padStart(2, '0')} &nbsp; {slide.title}
              </button>
            ))}
          </nav>

          <div className="awp-what-stage">
            <div className="awp-slide-wrap">
              {slides.map((slide, index) => (
                <div className={`awp-slide${index === current ? ' active' : ''}`} key={slide.key || index}>
                  <div className="awp-main-card">
                    <div className="awp-asset"><WhatSlideArt /></div>
                  </div>
                  <div className="awp-side-card s1"><div className="awp-asset"><WhatSideArt /></div></div>
                  <div className="awp-side-card s2"><div className="awp-asset"><WhatSideArt /></div></div>
                  <div className="awp-side-card s3"><div className="awp-asset"><WhatSideArt /></div></div>
                </div>
              ))}
            </div>
            <div className="awp-visual-caption">
              <span>{frameworkLabel}</span>
              <strong>{active.headline}</strong>
            </div>
          </div>

          <aside className="awp-desc-panel">
            <div className="awp-what-kicker">{active.kicker}</div>
            <div className="awp-what-title">{active.title}</div>
            <p className="awp-what-text">{active.copy}</p>
            <div className="awp-bottom-note">{active.note}</div>
            <a className="awp-what-action" href="#awp-offerings">
              Explore our services <span aria-hidden="true">&#8599;</span>
            </a>
            <div className="awp-dots">
              {slides.map((slide, index) => (
                  <button
                    type="button"
                    key={slide.key || index}
                    className={`awp-dot${index === current ? ' active' : ''}`}
                    aria-label={`Slide ${index + 1}`}
                    onClick={() => goTo(index)}
                  />
                ))}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function PremiumKrumago({ content }) {
  const domains = resolveItems(content.domains, DEFAULT_KRUMAGO_DOMAINS);
  const platformTitle = content.platformTitle || 'Platform';
  const platformSubtitle = content.platformSubtitle || 'Streamline every aspect of your business with our all-in-one management solution. Purpose-built for Education, Retail, Flex Office, and WAAS \u2014 under one powerful platform.';
  const platformStatValue = content.platformStatValue || '3+';
  const platformStatLabel = content.platformStatLabel || 'Core industries supported through one unified business management platform.';
  const platformHeadline = content.platformHeadline || 'Operate every domain with one connected platform.';
  const platformTags = Array.isArray(content.platformTags) && content.platformTags.length > 0
    ? content.platformTags
    : ['Education', 'Retail', 'Flex Office', 'WAAS'];

  return (
    <section className="awp-krumago" id="awp-platform" data-awp-motion="platform">
      <div className="awp-krumago-hero">
        <div className="awp-krumago-content">
          <div className="awp-krumago-top">
            <div>
              <div className="awp-krumago-logo">
                <span className="kru">Kru</span><span className="ma">Ma</span><span className="go">go</span>
              </div>
              <h2 className="awp-krumago-title">{platformTitle}</h2>
              <p className="awp-krumago-sub">{platformSubtitle}</p>
            </div>
            <div className="awp-krumago-stat">
              <strong>{platformStatValue}</strong>
              <span><b>{platformStatLabel}</b></span>
            </div>
          </div>

          <div className="awp-krumago-panel">
            <div className="awp-krumago-copy">
              <div className="awp-krumago-tags">
                {platformTags.map((tag) => <span key={tag}>{tag}</span>)}
              </div>
              <h2 className="awp-krumago-headline">{platformHeadline}</h2>
            </div>
            <div className="awp-krumago-domains">
              {domains.map((domain, index) => (
                <article className="awp-krumago-domain" key={domain.num || index} style={{ '--awp-delay': `${220 + index * 110}ms` }}>
                  <div className="awp-domain-number"><span>{domain.num || String(index + 1).padStart(2, '0')}</span><small>Business domain</small></div>
                  <b>{domain.title}</b>
                  <p>{domain.body || domain.description}</p>
                  <ul className="awp-domain-features">
                    {Array.isArray(domain.features) && domain.features.map((feature) => <li key={feature}>{feature}</li>)}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PremiumWho({ content, theme }) {
  const brandName = content.brandName || theme.brandName || 'AVPS Technologies';
  const whoTitle = content.whoTitle || 'Who\nWe Are';
  const whoBody = content.whoBody || `${brandName} combines enterprise implementation capability, domain expertise and team enablement to help organizations modernize with confidence.`;
  const stats = resolveItems(content.stats, DEFAULT_STATS);

  return (
    <section className="awp-who" id="awp-who" data-awp-motion="identity">
      <div className="awp-wrap awp-who-grid">
        <div className="awp-who-copy">
          <h2>{whoTitle.split('\n').map((line, i) => (i > 0 ? <React.Fragment key={i}><br />{line}</React.Fragment> : line))}</h2>
          <p>{whoBody}</p>
        </div>
        <div className="awp-stats">
          {stats.map((stat, index) => (
            <div className="awp-stat" key={stat.label || index} style={{ '--awp-delay': `${index * 90}ms` }}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PremiumFooter({ content, theme }) {
  const brandName = content.brandName || theme.brandName || 'AVPS Technologies';
  const footerIntro = content.footerIntro || 'Enterprise technology, IBM implementation, cloud modernization and managed services designed around lasting client ownership.';
  const footerAddress = content.footerAddress || 'Alpharetta, Georgia, USA';
  const footerPhone = content.footerPhone || '+1 (678) 855-6990';
  const footerEmail = content.footerEmail || 'info@avpstech.com';

  const exploreLinks = Array.isArray(content.footerExploreLinks) && content.footerExploreLinks.length > 0
    ? content.footerExploreLinks
    : [
      { label: 'About', route: '/about' },
      { label: 'Services', route: '/services' },
      { label: 'Products', route: '/products' },
      { label: 'Careers', route: '/careers' },
    ];
  const expertiseLinks = Array.isArray(content.footerExpertiseLinks) && content.footerExpertiseLinks.length > 0
    ? content.footerExpertiseLinks
    : [
      { label: 'IBM ODM', route: '/odm' },
      { label: 'Cloud Pak', route: '/pak' },
      { label: 'IBM Watson', route: '/watson' },
      { label: 'Managed IT', route: '/manageIt' },
      { label: 'Modernization', route: '/app-modernization' },
    ];

  return (
    <footer className="awp-footer" data-awp-motion="footer">
      <div className="awp-wrap awp-footer-main">
        <div style={{ '--awp-delay': '0ms' }}>
          <a className="awp-footer-brand" href="/" aria-label={`${brandName} home`}>
            <span className="awp-brand-mark" aria-hidden="true">{brandName.charAt(0).toUpperCase()}</span>
            <span>{brandName}</span>
          </a>
          <p className="awp-footer-intro">{footerIntro}</p>
        </div>
        <div style={{ '--awp-delay': '85ms' }}>
          <h3>{content.footerExploreTitle || 'Explore'}</h3>
          <nav className="awp-footer-links" aria-label="Footer pages">
            {exploreLinks.map((link) => <a key={link.route || link.label} href={link.route || '/'}>{link.label}</a>)}
          </nav>
        </div>
        <div style={{ '--awp-delay': '170ms' }}>
          <h3>{content.footerExpertiseTitle || 'Expertise'}</h3>
          <nav className="awp-footer-links" aria-label="Footer expertise">
            {expertiseLinks.map((link) => <a key={link.route || link.label} href={link.route || '/'}>{link.label}</a>)}
          </nav>
        </div>
        <div style={{ '--awp-delay': '255ms' }}>
          <h3>{content.footerContactTitle || "Let\u2019s talk"}</h3>
          <div className="awp-footer-contact">
            {footerAddress && <span>{footerAddress}</span>}
            {footerPhone && <a href={`tel:${footerPhone.replace(/[^+\d]/g, '')}`}>{footerPhone}</a>}
            {footerEmail && <a href={`mailto:${footerEmail}`}>{footerEmail}</a>}
            <a href="/contactus">{content.footerCtaLabel || 'Start a conversation'} &#8599;</a>
          </div>
        </div>
      </div>
      <div className="awp-wrap awp-footer-bottom">
        <span>© {new Date().getFullYear()} {brandName}. All rights reserved.</span>
        <div className="awp-footer-bottom-links"><a href="/contactus">Contact</a><a href="/sign-in">Sign in</a></div>
      </div>
    </footer>
  );
}

/**
 * The full premium WAAS home page section (reader/published side). Matches the
 * builder preview version byte-for-byte in markup + CSS so Preview <-> Reader
 * parity holds. Header/footer are NOT included here — the existing WAAS global
 * header/footer (already rendered by the page shell) wrap this section exactly
 * as they wrap every other WAAS section.
 *
 * @param {object} props
 * @param {object} [props.section] the Section record (unused for now, accepted
 *   for parity with every other WAAS section component's call signature)
 * @param {object} [props.theme] the resolved WAAS theme (brandName only used here)
 */
export default function WaasPremiumHomeSection({ section, theme = {} }) {
  const rootRef = useRef(null);
  const content = (section && typeof section.content === 'object' && section.content !== null)
    ? section.content
    : {};
  usePremiumMotion(rootRef);

  const fontFamily = content.fontFamily || theme.fontFamily || null;

  return (
    <div
      ref={rootRef}
      className="avps-waas-premium"
      data-waas-premium-home="true"
      style={fontFamily ? { fontFamily } : undefined}
    >
      <style>{PREMIUM_CSS}</style>
      <PremiumHeader content={content} theme={theme} />
      <PremiumHero content={content} theme={theme} />
      <PremiumOfferings content={content} />
      <PremiumWhat content={content} />
      <PremiumKrumago content={content} />
      <PremiumWho content={content} theme={theme} />
      <PremiumFooter content={content} theme={theme} />
    </div>
  );
}
